import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroChart } from '../waltero';
import { usePerformanceData } from '../../context/PerformanceDataContext';
import { hasOrganizationAccess } from '../../utils/auth';
import { ADMIN_ORGANIZATION_ID } from '../../config/api';
import { 
  getDigitPositionFailureData 
} from '../../utils/performanceDataProcessing';

/**
 * SuccessRateAnalytics Component
 * 
 * Extracted from PerformanceTab.js lines 2187-2469
 * Provides digit position failure analysis histograms with custom visualization
 * 
 * Features:
 * - Interactive digit position failure analysis
 * - Custom histogram visualization with adaptive scaling  
 * - Low confidence detection (score < 0.9)
 * - Hover data support for detailed position statistics
 */
const SuccessRateAnalytics = ({ onHoverData }) => {
  const theme = useTheme();
  const { performanceData } = usePerformanceData();

  // Process digit position failure data (from original lines 2189-2212)
  const digitPositionData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) {
      return null;
    }
    return getDigitPositionFailureData(performanceData);
  }, [performanceData]);

  // Pre-calculate chart data processing to avoid hooks rule violations
  const maxFailurePercentage = digitPositionData?.positions ? Math.max(...digitPositionData.positions.map(pos => pos.failurePercentage || 0)) : 0;
  const maxLowConfidencePercentage = digitPositionData?.positions ? Math.max(...digitPositionData.positions.map(pos => pos.lowConfidencePercentage || 0)) : 0;

  // ECharts configuration for Failure chart - must be before any conditional returns
  const failureChartOptions = useMemo(() => {
    console.log('🔧 Building failure chart options, digitPositionData:', digitPositionData);
    if (!digitPositionData?.positions) {
      console.log('🔧 No digit position data, returning minimal chart');
      return {
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [] }]
      };
    }

    const positions = digitPositionData.positions.map(pos => `Position ${pos.position}`);
    const failureData = digitPositionData.positions.map(pos => {
      const percentage = pos.failurePercentage || 0;
      return {
        value: percentage,
        name: `Position ${pos.position}`,
        position: pos.position,
        count: pos.failures || 0,
        total: pos.totalAttempts || 0,
        percentage: percentage
      };
    });

    console.log('📊 Failure Chart Data:', { 
      positions, 
      failureData, 
      maxFailurePercentage,
      hasData: failureData.length > 0,
      sampleData: failureData[0]
    });

    return {
      tooltip: {
        show: false // We'll use our custom overlay instead
      },
      xAxis: {
        type: 'category',
        data: positions,
        axisLabel: {
          rotate: 45,
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}%'
        },
        min: 0,
        max: Math.max(maxFailurePercentage * 1.1, 0.01) // Add 10% padding, minimum 0.01%
      },
      series: [{
        type: 'bar',
        data: failureData.map(item => item.value), // Use simple numeric values for bars
        itemStyle: {
          color: theme.palette.error.main
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params) => `${params.value.toFixed(2)}%`,
          fontSize: 10
        }
      }],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '10%'
      }
    };
  }, [digitPositionData, maxFailurePercentage, theme]);

  // ECharts configuration for Low Confidence chart - must be before any conditional returns
  const lowConfidenceChartOptions = useMemo(() => {
    console.log('🔧 Building low confidence chart options, digitPositionData:', digitPositionData);
    if (!digitPositionData?.positions) {
      console.log('🔧 No digit position data, returning minimal chart');
      return {
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [] }]
      };
    }

    const positions = digitPositionData.positions.map(pos => `Position ${pos.position}`);
    const lowConfidenceData = digitPositionData.positions.map(pos => {
      const percentage = pos.lowConfidencePercentage || 0;
      return {
        value: percentage,
        name: `Position ${pos.position}`,
        position: pos.position,
        count: pos.lowConfidence || 0,
        total: pos.totalAttempts || 0,
        percentage: percentage
      };
    });

    console.log('📊 Low Confidence Chart Data:', { 
      positions, 
      lowConfidenceData, 
      maxLowConfidencePercentage,
      hasData: lowConfidenceData.length > 0,
      sampleData: lowConfidenceData[0]
    });

    return {
      tooltip: {
        show: false // We'll use our custom overlay instead
      },
      xAxis: {
        type: 'category',
        data: positions,
        axisLabel: {
          rotate: 45,
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}%'
        },
        min: 0,
        max: Math.max(maxLowConfidencePercentage * 1.1, 0.01) // Add 10% padding, minimum 0.01%
      },
      series: [{
        type: 'bar',
        data: lowConfidenceData.map(item => item.value), // Use simple numeric values for bars
        itemStyle: {
          color: theme.palette.warning.main
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params) => `${params.value.toFixed(2)}%`,
          fontSize: 10
        }
      }],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '10%'
      }
    };
  }, [digitPositionData, maxLowConfidencePercentage, theme]);

  // Only show if user has admin access (but success rate data comes from main API call)
  if (!hasOrganizationAccess(ADMIN_ORGANIZATION_ID)) {
    return null;
  }
  
  // ALWAYS render the component - never return null here since it breaks the success tab
  // The individual sections can show "no data" messages instead
  
  console.log('📊 SuccessRateAnalytics admin check passed, processing data...');

  // Check if we have valid data - but still show the component with message
  const hasValidData = digitPositionData && digitPositionData.positions && digitPositionData.positions.length > 0;
  const hasNonZeroData = hasValidData && digitPositionData.positions.some(pos => 
    (pos.totalAttempts || 0) > 0 || (pos.failurePercentage || 0) > 0 || (pos.lowConfidencePercentage || 0) > 0
  );

  console.log('📊 Digit Position Data Analysis:', {
    hasValidData,
    hasNonZeroData,
    positionsCount: digitPositionData?.positions?.length || 0,
    samplePosition: digitPositionData?.positions?.[0],
    maxFailure: maxFailurePercentage,
    maxLowConf: maxLowConfidencePercentage,
    willRenderCharts: hasNonZeroData
  });


  // Debug the actual data values - with null safety
  if (digitPositionData?.positions) {
    console.log(`📊 Digit Position Failure Data:`, digitPositionData.positions.map(pos => ({
      position: pos.position,
      failurePercentage: pos.failurePercentage,
      lowConfidencePercentage: pos.lowConfidencePercentage,
      failures: pos.failures,
      totalAttempts: pos.totalAttempts
    })));
  }

  // More aggressive scaling - ensure we can see differences even for very small values
  const failureValues = digitPositionData?.positions?.map(pos => pos.failurePercentage || 0) || [];
  const lowConfidenceValues = digitPositionData?.positions?.map(pos => pos.lowConfidencePercentage || 0) || [];

  // If all values are the same, create artificial differences for visualization
  const allFailuresSame = failureValues.every(val => val === failureValues[0]);
  const allLowConfidenceSame = lowConfidenceValues.every(val => val === lowConfidenceValues[0]);

  console.log(`📊 Analysis - Max failure: ${(maxFailurePercentage || 0).toFixed(4)}%, Max low confidence: ${(maxLowConfidencePercentage || 0).toFixed(4)}%`);
  console.log(`📊 All failures same: ${allFailuresSame}, All low confidence same: ${allLowConfidenceSame}`);

  const handlePositionHover = (position, data, event) => {
    if (onHoverData && data) {
      onHoverData({
        data: {
          ...data,
          position
        },
        position: {
          x: event.clientX || event.pageX || 0,
          y: event.clientY || event.pageY || 0
        }
      });
    }
  };

  const handleMouseLeave = () => {
    if (onHoverData) {
      onHoverData(null);
    }
  };

  // ECharts event handlers
  const handleFailureChartEvents = {
    mouseover: (params) => {
      console.log('🎯 Failure chart mouseover:', params);
      const dataIndex = params.dataIndex;
      const originalData = digitPositionData?.positions?.[dataIndex];
      if (originalData) {
        // Use global mouse position instead of chart-relative coordinates
        handlePositionHover(originalData.position, {
          type: 'failure',
          percentage: originalData.failurePercentage || 0,
          count: originalData.failures || 0,
          total: originalData.totalAttempts || 0
        }, { 
          clientX: params.event?.event?.clientX || params.event?.clientX || 0, 
          clientY: params.event?.event?.clientY || params.event?.clientY || 0 
        });
      }
    },
    mouseout: () => {
      console.log('🎯 Failure chart mouseout');
      handleMouseLeave();
    }
  };

  const handleLowConfidenceChartEvents = {
    mouseover: (params) => {
      console.log('🎯 Low confidence chart mouseover:', params);
      const dataIndex = params.dataIndex;
      const originalData = digitPositionData?.positions?.[dataIndex];
      if (originalData) {
        // Use global mouse position instead of chart-relative coordinates
        handlePositionHover(originalData.position, {
          type: 'lowConfidence',
          percentage: originalData.lowConfidencePercentage || 0,
          count: originalData.lowConfidence || 0,
          total: originalData.totalAttempts || 0
        }, { 
          clientX: params.event?.event?.clientX || params.event?.clientX || 0, 
          clientY: params.event?.event?.clientY || params.event?.clientY || 0 
        });
      }
    },
    mouseout: () => {
      console.log('🎯 Low confidence chart mouseout');
      handleMouseLeave();
    }
  };


  // If no non-zero data, show informational message instead of blank screen
  if (!hasNonZeroData) {
    return (
      <div className="digit-analysis-widget">
        <WalteroTypography variant="h6" gutterBottom>
          Digit Position Analysis
        </WalteroTypography>
        <WalteroCard 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            border: `2px dashed ${theme.palette.waltero.primary.neutralBlue}`,
            backgroundColor: theme.palette.waltero.secondary.backgroundApricot
          }}
        >
          <WalteroTypography variant="body1" color="primary.main">
            No digit position data available for the selected time period.
          </WalteroTypography>
          <WalteroTypography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            This analysis requires devices with recent transmission data to display digit reading accuracy statistics.
          </WalteroTypography>
          {digitPositionData?.totalReadings > 0 && (
            <WalteroTypography variant="caption" sx={{ mt: 2, display: 'block' }}>
              Found {digitPositionData.totalReadings} readings but no position-specific data available.
            </WalteroTypography>
          )}
        </WalteroCard>
      </div>
    );
  }

  return (
    <div className="digit-analysis-widget">
      <WalteroTypography variant="h6" gutterBottom>
        Digit Position Analysis
      </WalteroTypography>
      
      {/* Failures Histogram */}
      <WalteroCard sx={{ mb: 3, p: 3 }}>
        <WalteroTypography variant="subtitle1" gutterBottom>
          Failures (digit = -1) - Max: {(maxFailurePercentage || 0).toFixed(4)}%
        </WalteroTypography>
        <WalteroChart
          option={failureChartOptions}
          height={300}
          events={handleFailureChartEvents}
        />
      </WalteroCard>

      {/* Low Confidence Histogram */}
      <WalteroCard sx={{ mb: 3, p: 3 }}>
        <WalteroTypography variant="subtitle1" gutterBottom>
          Low Confidence (score &lt; 0.9) - Max: {(maxLowConfidencePercentage || 0).toFixed(4)}%
        </WalteroTypography>
        <WalteroChart
          option={lowConfidenceChartOptions}
          height={300}
          events={handleLowConfidenceChartEvents}
        />
      </WalteroCard>

      <WalteroCard variant="outlined" sx={{ p: 3 }}>
        <div className="digit-analysis-summary">
          <WalteroTypography variant="body2">
            Total readings analyzed: {digitPositionData.totalReadings || 0}
          </WalteroTypography>
          <WalteroTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Note: When all values are identical, artificial variation is added for visual comparison. Hover for exact percentages.
          </WalteroTypography>
        </div>
      </WalteroCard>
    </div>
  );
};

SuccessRateAnalytics.propTypes = {
  onHoverData: PropTypes.func
};

SuccessRateAnalytics.defaultProps = {
  onHoverData: () => {}
};

export default SuccessRateAnalytics;